<script setup lang="ts">
import WrImageContent from "~/components/global/WrImageContent.vue";
import WrTag from "~/components/global/WrTag.vue";

const route = useRoute();
const slug = route.params.slug;

const config = useRuntimeConfig();
const baseURL = config.public.baseUrl;
const { data, error } = await useFetch(
  `${baseURL}/content/career/${slug}-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

useHead({
  title: data.value?.meta_tags?.title || "",
  meta: data.value?.meta_tags?.meta || [],
});
const text = `<span
                    class="hover:no-underline underline cursor-pointer"
                    @click="openModal"
                    >Click here</span
                  >
                  to start your professional journey`;
const visible = ref(false);

const openModal = () => {
  visible.value = true;
};
</script>

<template>
  <section class="mt-10 z-10 relative">
    <div class="max-w-screen-lg px-4 mx-auto pb-10">
      <div
        class="dark:block dark:bg-[linear-gradient(91.95deg,_#51277B_4.5%,_#8F5BC4_96.91%)] shadow-[1px_2px_5px_0px_#00000026] rounded-full max-w-max"
      >
        <WrTag :tag="data.header.job" />
      </div>
      <h1>
        <WrGradientText
          append-class="pt-2 bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-3.57%,_var(--text-gradient-2)_20.72%)]"
        >
          {{ data.header.title }}</WrGradientText
        >
      </h1>
      <div class="text-lg tracking-[0.5px]" v-html="data.header.subtitle"></div>
      <div
        class="flex max-md:flex-col justify-between nd:items-center mt-6 gap-4 text-sm"
      >
        <span> {{ data.header.exp }} years </span>
        <span> {{ data.header.qualification }} </span>
      </div>
      <div class="flex flex-wrap gap-2 items-center mt-5">
        <div
          v-for="tech in data.header.skill.split(',')"
          class="dark:block dark:bg-[linear-gradient(91.95deg,_#51277B_4.5%,_#8F5BC4_96.91%)] bg-transparent shadow-[1px_2px_5px_0px_#00000026] rounded-full max-w-max"
        >
          <WrTag :tag="tech" />
        </div>
      </div>
      <div
        class="flex max-md:flex-col gap-4 justify-between md:items-center mt-5"
      >
        <div
          class="gradient-border-mask relative before:rounded-full before:bg-[linear-gradient(95.01deg,_#BDB1FF_2.72%,_#6952ED_95.35%)] max-w-max"
        >
          <WrButton
            link="/career"
            outlined
            append-class="z-10 relative"
            type="button"
          >
            Back to Career
          </WrButton>
        </div>
        <div class="inline-flex items-center gap-1">
          <WrButton :link="data.header.apply_link" target="_blank"
            >Appply Now</WrButton
          >
          <span class="text-sm text-center sm:text-left">
            or call us:
            <span class="font-bold">
              <a
                href="tel:+91 794 344 4427"
                class="cursor-pointer text-[#5f4ca4]"
              >
                +91 794 344 4427
              </a>
            </span>
          </span>
        </div>
      </div>
    </div>
  </section>

<section
    class="max-w-screen-lg px-4 mt-20 relative z-20 mx-auto"
  >
      <h2>
        <WrGradientText>{{ data.apply.descriptionTitle }}</WrGradientText>
      </h2>
      <div class="md:text-lg !leading-[1.7] tracking-[0.5px] *:list-disc *:space-y-2.5 *:pl-4" v-html="data.apply.descriptionText"></div>

      <h2 class="mt-10">
        <WrGradientText>{{ data.apply.skillTitle }}</WrGradientText>
      </h2>
      <div v-html="data.apply.skillDescription" class="*:md:text-lg *:tracking-[0.5px] *:list-disc *:pl-4 *:space-y-2.5"></div>
  </section>

  <section
    class="max-w-screen-xl px-4 sm:px-10 mt-20 relative z-20 mx-auto"
  >
      <WrImageContent
        :header="'Ready to join us?'"
        :text="text"
        src="/images/career-right.webp"
        height="574"
        width="467"
        :whyChooseUs="data?.apply"
        @openModal="openModal"
      />
  </section>

  <section
    class="relative pt-10 overflow-hidden max-w-screen-xl mx-auto px-4 z-10"
  >
    <WrTeamImageScroller />

    <div class="lg:pt-[128px] pt-16 relative z-10">
      <h2 class="text-center">
        <WrGradientText text="Trusted for excellence and efficiency" />
        <p class="text-lg text-[var(--foreground)]">
          Webreinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h2>
      <WrTestimonialCardCarousel class="mt-12" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </div>
  </section>
  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />
</template>
